import { ExpandablePanel } from '@dealstack/frontend/shared/components/ExpandablePanel/ExpandablePanel';
import { DropdownInput } from '@dealstack/frontend/shared/components/DropdownInput/DropdownInput';
import { useDropdownInput } from '@dealstack/frontend/shared/components/DropdownInput/useDropdownInput';
import { NumberInput } from '@dealstack/frontend/shared/components/NumberInput/NumberInput';
import { IconDeviceFloppy, IconX } from '@tabler/icons-react';
import { Controller } from 'react-hook-form';
import {
	CreateVestingTermStraightLineDto,
	UpdateVestingTermStraightLineDto,
	VestingTermStraightLineRes,
} from '@dealstack/shared/lib/generated';
import { useTranslationCommonUi } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { useEffect } from 'react';
import { useStraightLineTermController } from '@dealstack/frontend/shared/components/VestingTerms/StraightLineTerm/useStraightLineTermController';
import { generateUniformVestingMessage } from '@dealstack/frontend/shared/components/VestingTerms/helpers/vesting-messages';
import styled from 'styled-components';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds.ts';

export const StraightLineTerm = ({
	vestingTermId,
	vestingTermStraightLine,
	onCreateVestingTermStraightLine,
	onUpdateVestingTermStraightLine,
}: {
	vestingTermId: string;
	vestingTermStraightLine?: VestingTermStraightLineRes;
	onCreateVestingTermStraightLine: (
		vestingTermStraighLine: CreateVestingTermStraightLineDto
	) => void;
	onUpdateVestingTermStraightLine: (
		straightLineTermId: string,
		formValues: UpdateVestingTermStraightLineDto
	) => void;
}) => {
	const tCommonUi = useTranslationCommonUi();

	const {
		onSubmit,
		onCancel,
		isExpanded,
		toggleExpanded,
		handleSubmit,
		control,
		setValue,
		errors,
	} = useStraightLineTermController(
		onCreateVestingTermStraightLine,
		onUpdateVestingTermStraightLine,
		vestingTermId,
		vestingTermStraightLine
	);

	const cardTitle = generateUniformVestingMessage(tCommonUi, vestingTermStraightLine);

	const periodTimeUnitOptions = [
		{ label: tCommonUi('vesting_terms.monthly'), value: 'MONTHLY' },
		{ label: tCommonUi('vesting_terms.yearly'), value: 'YEARLY' },
	];

	const { value, onChange } = useDropdownInput({
		initialValue: vestingTermStraightLine?.periodTimeUnit || periodTimeUnitOptions[0].value,
		options: periodTimeUnitOptions,
	});

	useEffect(() => {
		setValue('periodTimeUnit', value as VestingTermStraightLineRes['periodTimeUnit']);
	}, [value, setValue]);

	return (
		<ExpandablePanel
			title={cardTitle}
			isExpanded={isExpanded || !vestingTermStraightLine}
			onExpandClick={() => toggleExpanded()}
			dataTestId={TEST_ELEMENT_IDS.VESTING_TERMS_EXPANDABLE_PANEL}
		>
			<form className="p-4" onSubmit={handleSubmit(onSubmit)}>
				<div className="flex justify-start w-full">
					<div className="w-1/2 mr-4">
						<div className="font-semibold">{tCommonUi('vesting_terms.unit_label')}</div>
						<StyledDivWithOutline tabIndex={1}>
							<DropdownInput
								options={periodTimeUnitOptions}
								value={value}
								onChange={onChange}
								dataTestId={TEST_ELEMENT_IDS.VESTING_TERMS_TIME_UNIT_DROPDOWN}
							/>
						</StyledDivWithOutline>
						{errors.period?.message && <div className="h-7"></div>}

						<div className="font-semibold mt-2">
							{tCommonUi('vesting_terms.interval_label')}
						</div>

						<Controller
							name="interval"
							control={control}
							render={({ field }) => (
								<NumberInput
									inputPlaceholder=""
									{...field}
									tabIndex={3}
									data-testid={TEST_ELEMENT_IDS.VESTING_TERMS_INTERVAL_INPUT}
									errorMessage={
										errors.interval?.message &&
										errors.interval.message.toString()
									}
								/>
							)}
						/>
					</div>

					<div className="w-1/2">
						<div className="font-semibold">
							{tCommonUi('vesting_terms.period_label')}
						</div>

						<Controller
							name="period"
							control={control}
							render={({ field }) => (
								<NumberInput
									inputPlaceholder=""
									{...field}
									tabIndex={2}
									dataTestId={TEST_ELEMENT_IDS.VESTING_TERMS_PERIOD_INPUT}
									errorMessage={
										errors.period?.message && errors.period.message.toString()
									}
								/>
							)}
						/>

						<div className="font-semibold  mt-2">
							{tCommonUi('vesting_terms.cliff_label')}
						</div>

						<Controller
							name="cliff"
							control={control}
							render={({ field }) => (
								<NumberInput
									inputPlaceholder=""
									{...field}
									tabIndex={4}
									data-testid={TEST_ELEMENT_IDS.VESTING_TERMS_CLIFF_INPUT}
									errorMessage={
										errors.cliff?.message && errors.cliff.message.toString()
									}
								/>
							)}
						/>
					</div>
				</div>

				<div className="flex justify-between mt-4">
					{vestingTermStraightLine && (
						<button
							className="bg-white border-0 text-red-500 hover:text-red-700 flex items-center justify-end font-bold text-sm"
							onClick={onCancel}
							tabIndex={6}
							data-testid={TEST_ELEMENT_IDS.VESTING_TERMS_CANCEL_BUTTON}
						>
							<IconX stroke={2} size={24} className="mr-1" />{' '}
							{tCommonUi('vesting_terms.cancel')}
						</button>
					)}
					<button
						className="bg-blue-600 hover:bg-blue-700 flex items-center text-white px-2 py-2 font-semibold rounded border-none"
						type="submit"
						tabIndex={5}
						data-testid={TEST_ELEMENT_IDS.VESTING_TERMS_SAVE_BUTTON}
					>
						<IconDeviceFloppy stroke={2} size={24} className="mr-1" />
						{tCommonUi('vesting_terms.save')}
					</button>
				</div>
			</form>
		</ExpandablePanel>
	);
};

const StyledDivWithOutline = styled.div`
	transition: all 0.2s ease;
	border-radius: 0.25rem;
	&:focus {
		outline: 1px solid ${({ theme }) => theme.v3.colors.blue[2]};
	}
`;
